import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Loader2, Sparkles, RefreshCw, Trash2, AlertTriangle, CheckCircle2, Quote, Lightbulb, ListChecks, Phone, Clock, Download } from 'lucide-react';
import { useT } from '../../utils/translate';
import { srv } from '../../utils/serverText';
import { localeShortDateTime } from '../../utils/date';
import { useCallAiSettings, invalidateBrief, IN_PROGRESS, scoreTone, itemTitle, fmtClock } from '../../utils/callAi';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const STEPS = ['queued', 'downloading', 'transcribing', 'analyzing', 'done'];
const stepIndex = (s) => ({ queued: 0, downloading: 1, downloaded: 1, transcribing: 2, transcribed: 2, analyzing: 3, done: 4 }[s] ?? 0);

export function ScoreRing({ score, size = 76 }) {
  const tone = scoreTone(score);
  const r = (size - 10) / 2, c = 2 * Math.PI * r, pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone.ring} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(pct / 100) * c} ${c}`} />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-xl font-bold ${tone.text}`}>{score == null ? '—' : score}</span>
    </div>
  );
}

const SENT_CLS = { positive: 'bg-emerald-50 text-emerald-700', neutral: 'bg-surface-100 text-ink-secondary', negative: 'bg-red-50 text-red-600' };
const SEV_CLS = { low: 'bg-surface-100 text-ink-secondary', medium: 'bg-amber-50 text-amber-700', high: 'bg-red-50 text-red-600' };

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-primary-600" />}{title}</h3>
      {children}
    </section>
  );
}

function Transcript({ item }) {
  const t = useT();
  const audioRef = useRef(null);
  const [src, setSrc] = useState('');
  const [audioErr, setAudioErr] = useState(false);
  const [now, setNow] = useState(0);
  const segs = useMemo(() => item.transcript?.segments || [], [item.transcript?.segments]);

  useEffect(() => {
    let url = '';
    let alive = true;
    if (item.audio?.key) {
      axios.get(`${API}/call-analysis/${item._id}/audio`, { responseType: 'blob' })
        .then(r => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => { if (alive) { setAudioErr(true); if (item.audio?.sourceUrl) setSrc(item.audio.sourceUrl); } });
    } else if (item.audio?.sourceUrl) setSrc(item.audio.sourceUrl);
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [item._id, item.audio?.key, item.audio?.sourceUrl]);

  const active = useMemo(() => { let a = -1; segs.forEach((s, i) => { if (s.start <= now + 0.2) a = i; }); return a; }, [segs, now]);
  const seek = (s) => { const a = audioRef.current; if (!a) return; a.currentTime = s.start; a.play().catch(() => {}); };

  if (!segs.length) return <p className="text-sm text-ink-tertiary py-8 text-center">{t('callAI.modal.noTranscript')}</p>;
  return (
    <div className="space-y-3">
      {src && (
        <div className="flex items-center gap-2">
          <audio ref={audioRef} src={src} controls preload="metadata" className="flex-1 min-w-0 h-10" onTimeUpdate={e => setNow(e.currentTarget.currentTime)} onError={() => setAudioErr(true)} />
          <a href={src} download={`call-${item._id}.mp3`} target="_blank" rel="noreferrer" title={t('loc.callRecordingPlayer.download')}
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-ink-tertiary hover:text-primary-600 hover:bg-primary-50 transition-colors">
            <Download className="w-4 h-4" />
          </a>
        </div>
      )}
      {audioErr && !src && <p className="text-xs text-red-600">{t('callAI.modal.audioError')}</p>}
      {item.analysis?.rolesInferred && <p className="text-xs text-ink-tertiary">{t('callAI.modal.rolesInferred')}</p>}
      <div className="space-y-2">
        {segs.map((s, i) => {
          const isAgent = s.role === 'agent', isClient = s.role === 'client';
          return (
            <div key={i} className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
              <button type="button" onClick={() => seek(s)}
                className={`max-w-[85%] text-left rounded-2xl px-3.5 py-2 border transition-colors ${
                  i === active ? 'ring-2 ring-primary-300 ' : ''}${isAgent ? 'bg-primary-50 border-primary-100 rounded-tl-sm' : isClient ? 'bg-white border-surface-200 rounded-tr-sm' : 'bg-surface-50 border-surface-200'}`}>
                <div className="flex items-center gap-2 text-xs text-ink-tertiary mb-0.5">
                  <span className="font-semibold">{t('callAI.modal.' + (isAgent ? 'agent' : isClient ? 'client' : 'unknown'))}</span>
                  <span className="font-mono">{fmtClock(s.start)}</span>
                  {s.lang && <span className="uppercase">{s.lang}</span>}
                </div>
                <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap break-words">{s.text}</p>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Report({ item, defaults }) {
  const t = useT();
  const a = item.analysis || {};
  const m = a.metrics || {};
  const metricRows = ['agentTalkPct', 'silencePct', 'interruptions', 'avgResponseGapSec', 'agentWpm', 'longestAgentMonologueSec'].filter(k => m[k] != null);
  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <ScoreRing score={item.scores?.total} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {a.outcome && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-50 text-primary-700">{t('callAI.outcome.' + a.outcome)}</span>}
            {a.sentiment?.client && <span className={`text-xs font-medium px-2 py-1 rounded-full ${SENT_CLS[a.sentiment.client]}`}>{t('callAI.modal.clientSentiment')}: {t('callAI.sentiment.' + a.sentiment.client)}</span>}
            {a.sentiment?.agent && <span className={`text-xs font-medium px-2 py-1 rounded-full ${SENT_CLS[a.sentiment.agent]}`}>{t('callAI.modal.agentSentiment')}: {t('callAI.sentiment.' + a.sentiment.agent)}</span>}
            {a.problem?.flagged && <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600"><AlertTriangle className="w-3 h-3" />{t('callAI.badge.problem')}</span>}
          </div>
          {a.summary && <p className="text-sm text-ink leading-relaxed">{a.summary}</p>}
          {a.outcomeNote && <p className="text-xs text-ink-tertiary">{a.outcomeNote}</p>}
        </div>
      </div>

      {a.tooShort && <p className="text-sm text-ink-secondary bg-surface-50 rounded-xl px-4 py-3">{t('callAI.modal.tooShortNote')}</p>}

      {a.clientIntent && <Section title={t('callAI.modal.intent')}><p className="text-sm text-ink-secondary">{a.clientIntent}</p></Section>}

      {a.checklist?.length > 0 && (
        <Section icon={ListChecks} title={t('callAI.modal.checklist')}>
          <div className="border border-surface-200 rounded-xl divide-y divide-surface-100 overflow-hidden">
            {a.checklist.map(c => {
              const pct = c.applicable ? Math.round((c.score / 2) * 100) : null;
              const tn = scoreTone(pct);
              return (
                <div key={c.key} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex-1 min-w-0 text-sm font-medium text-ink">{itemTitle(c, t, defaults)}</span>
                    {c.applicable
                      ? <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tn.bg} ${tn.text}`}>{c.score === 2 ? '✓' : c.score === 1 ? '≈' : '✗'} {pct}%</span>
                      : <span className="text-xs text-ink-tertiary">{t('callAI.modal.notApplicable')}</span>}
                  </div>
                  {c.applicable && <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden mt-2"><div className={`h-full rounded-full ${tn.bar}`} style={{ width: `${pct}%` }} /></div>}
                  {c.comment && <p className="text-xs text-ink-secondary mt-1.5">{c.comment}</p>}
                  {c.evidence && <p className="text-xs text-ink-tertiary mt-1 flex gap-1.5"><Quote className="w-3 h-3 shrink-0 mt-0.5" /><span className="italic">{c.evidence}</span></p>}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <Section icon={AlertTriangle} title={t('callAI.modal.redFlags')}>
        {a.redFlags?.length ? (
          <div className="space-y-2">
            {a.redFlags.map((f, i) => (
              <div key={i} className="rounded-xl border border-red-100 bg-red-50/40 px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-ink">{t('callAI.flag.' + f.type)}</span>
                  <span className={`text-xs font-medium px-1.5 py-px rounded-full ${SEV_CLS[f.severity]}`}>{t('callAI.severity.' + f.severity)}</span>
                  {f.at != null && <span className="text-xs font-mono text-ink-tertiary">{fmtClock(f.at)}</span>}
                </div>
                <p className="text-sm text-ink-secondary mt-1">{f.text}</p>
                {f.quote && <p className="text-xs text-ink-tertiary italic mt-1">“{f.quote}”</p>}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{t('callAI.modal.noRedFlags')}</p>}
      </Section>

      {a.actionItems?.length > 0 && (
        <Section icon={Clock} title={t('callAI.modal.actions')}>
          <ul className="space-y-1.5">
            {a.actionItems.map((x, i) => <li key={i} className="text-sm text-ink flex gap-2"><span className="text-primary-500">•</span><span>{x.text}{x.dueHint && <span className="text-ink-tertiary"> — {x.dueHint}</span>}</span></li>)}
          </ul>
        </Section>
      )}

      {a.coaching?.length > 0 && (
        <Section icon={Lightbulb} title={t('callAI.modal.coaching')}>
          <ul className="space-y-1.5">{a.coaching.map((x, i) => <li key={i} className="text-sm text-ink-secondary flex gap-2"><span className="text-amber-700">•</span><span>{x}</span></li>)}</ul>
        </Section>
      )}

      {a.topics?.length > 0 && (
        <Section title={t('callAI.modal.topics')}>
          <div className="flex flex-wrap gap-1.5">{a.topics.map((x, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-surface-100 text-ink-secondary">{x}</span>)}</div>
        </Section>
      )}

      {metricRows.length > 0 && (
        <Section title={t('callAI.modal.metrics')}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {metricRows.map(k => (
              <div key={k} className="rounded-xl bg-surface-50 px-3 py-2.5">
                <p className="text-xs text-ink-tertiary leading-tight">{t('callAI.metric.' + k)}</p>
                <p className="text-lg font-bold text-ink mt-0.5">{m[k]}{k.endsWith('Pct') ? '%' : ''}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// Окно «AI-разбор звонка»: состояние анализа → отчёт по чек-листу → расшифровка с проигрыванием
export default function CallAnalysisModal({ callId, call, onClose }) {
  const t = useT();
  const { data: cfg, settings, engine, canEdit } = useCallAiSettings();
  const [item, setItem] = useState(undefined);       // undefined — грузим, null — анализа нет
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('report');

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/call-analysis/by-call/${callId}`); setItem(r.data.item || null); return r.data.item; }
    catch { setItem(null); return null; }
  }, [callId]);

  useEffect(() => { load(); }, [load]);
  const status = item?.status;
  useEffect(() => {
    if (!status || !IN_PROGRESS.includes(status)) { if (status) invalidateBrief(callId); return undefined; }
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [status, load, callId]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); invalidateBrief(callId); }
    catch (e) {
      const reason = e.response?.data?.reason;
      toast.error(reason ? t('callAI.status.skipped') + ': ' + reason : (e.response?.data?.message || t('callAI.modal.error')));
    } finally { setBusy(false); }
  };
  const analyze = () => act(() => axios.post(`${API}/call-analysis/call/${callId}`));
  const retry = () => act(() => axios.post(`${API}/call-analysis/${item._id}/retry`));
  const reanalyze = () => act(() => axios.post(`${API}/call-analysis/${item._id}/reanalyze`));
  const remove = async () => {
    if (!window.confirm(t('callAI.modal.deleteConfirm'))) return;
    await act(() => axios.delete(`${API}/call-analysis/${item._id}`));
    onClose();
  };

  const defaults = cfg?.defaultChecklist;
  const done = status === 'done';
  const meta = item?.callMeta;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-3xl flex flex-col max-h-[92dvh]">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-surface-100 shrink-0">
          <div className="min-w-0">
            <h2 className="font-semibold text-ink flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary-600" />{t('callAI.modal.title')}</h2>
            <p className="text-xs text-ink-tertiary mt-0.5 flex flex-wrap items-center gap-x-3">
              {(call?.phone || meta?.phone) && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{call?.phone || meta?.phone}</span>}
              {(call?.contact?.name || item?.callMeta?.contact?.name) && <span>{srv(call?.contact?.name || item.callMeta.contact.name)}</span>}
              {(meta?.startedAt || call?.startedAt) && <span>{localeShortDateTime(meta?.startedAt || call?.startedAt)}</span>}
              {meta?.agent?.name && <span>{meta.agent.name}</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>

        {done && (
          <div className="px-5 pt-3 shrink-0 flex gap-1 border-b border-surface-100">
            {['report', 'transcript'].map(k => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-primary-600 text-primary-700' : 'border-transparent text-ink-tertiary hover:text-ink'}`}>
                {t('callAI.modal.' + (k === 'report' ? 'tabReport' : 'tabTranscript'))}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {item === undefined || cfg === undefined ? (
            <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-primary-400" /></div>
          ) : item === null ? (
            <div className="text-center py-10 space-y-3">
              <Sparkles className="w-10 h-10 text-primary-300 mx-auto" />
              <p className="font-semibold text-ink">{t('callAI.modal.notAnalyzed')}</p>
              {settings?.enabled ? (
                <>
                  <p className="text-sm text-ink-tertiary max-w-sm mx-auto">{t('callAI.modal.notAnalyzedHint')}</p>
                  {engine && (!engine.stt || !engine.llm) && <p className="text-xs text-amber-700 max-w-sm mx-auto">{t('callAI.modal.noEngine')}</p>}
                  <button onClick={analyze} disabled={busy} className="btn-primary btn-md inline-flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{busy ? t('callAI.modal.starting') : t('callAI.modal.analyze')}
                  </button>
                </>
              ) : <p className="text-sm text-ink-tertiary max-w-sm mx-auto">{t('callAI.modal.disabled')}</p>}
            </div>
          ) : IN_PROGRESS.includes(status) ? (
            <div className="py-10 max-w-md mx-auto space-y-5">
              <div className="flex items-center justify-center gap-2 text-ink"><Loader2 className="w-4 h-4 animate-spin text-primary-500" /><span className="font-medium">{t('callAI.status.' + status)}</span></div>
              <div className="flex items-center gap-1.5">
                {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIndex(status) ? 'bg-primary-500' : 'bg-surface-200'}`} />)}
              </div>
              {item.error && <p className="text-xs text-amber-700 text-center">{item.error}</p>}
              {engine && ((status === 'downloaded' && !engine.stt) || (status === 'transcribed' && !engine.llm)) && <p className="text-xs text-amber-700 text-center">{t('callAI.modal.noEngine')}</p>}
            </div>
          ) : status === 'failed' ? (
            <div className="text-center py-10 space-y-3">
              <AlertTriangle className="w-10 h-10 text-red-600 mx-auto" />
              <p className="font-semibold text-ink">{t('callAI.status.failed')}</p>
              {item.error && <p className="text-xs text-ink-tertiary max-w-md mx-auto break-words">{item.error}</p>}
              <button onClick={retry} disabled={busy} className="btn-secondary btn-md inline-flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}{t('callAI.modal.retry')}</button>
            </div>
          ) : done ? (
            tab === 'report' ? <Report item={item} defaults={defaults} /> : <Transcript item={item} />
          ) : (
            <p className="text-center text-sm text-ink-tertiary py-10">{t('callAI.status.' + status)}</p>
          )}
        </div>

        {item && (done || status === 'failed') && (
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-surface-100 shrink-0">
            <div className="flex items-center gap-2">
              {done && item.transcript?.segments?.length > 0 && canEdit && (
                <button onClick={reanalyze} disabled={busy} className="btn-secondary btn-sm inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />{t('callAI.modal.reanalyze')}</button>
              )}
              {canEdit && <button onClick={remove} disabled={busy} className="text-sm text-red-600 hover:text-red-600 inline-flex items-center gap-1.5 px-2"><Trash2 className="w-3.5 h-3.5" />{t('callAI.modal.delete')}</button>}
            </div>
            <button onClick={onClose} className="btn-secondary btn-md">{t('callAI.modal.close')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
